import { useEffect, useMemo } from 'react'

import { RootState } from '@editor/store/app'
import { useThree } from '@react-three/fiber'
import { useSelector } from 'react-redux'
import { Matrix4, Vector3 } from 'three'

import { Mode } from '../../definitions'
import useProjectionPlane from '../../hooks/useProjectionPlane'
import useCanvasGrid from './hooks/useCanvasGrid'

const CanvasGrid = ({ size }: { size: number }) => {
  // Store
  const selectedBlueprint = useSelector((state: RootState) => state.blueprint.editor.selectedBlueprint)
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)
  const gridOpacity = useSelector((state: RootState) => state.blueprint.editor.gridOpacity)

  // Hooks
  const { camera, gl } = useThree()
  const { projectionMatrix } = useProjectionPlane(camera, gl)

  /**
   * Use projection matrix and target to position the grid correctly.
   */
  const matrix = useMemo(() => {
    if (!selectedBlueprint?.camera_profile?.target) return new Matrix4()

    const { target } = selectedBlueprint.camera_profile

    return projectionMatrix.clone().setPosition(new Vector3().fromArray(target))
  }, [projectionMatrix, selectedBlueprint?.camera_profile])

  /**
   * Make grid mesh
   */
  const { makeGridMesh } = useCanvasGrid(size, gridOpacity)
  const gridMesh = useMemo(() => makeGridMesh('canvas-grid', matrix), [makeGridMesh, matrix])

  // Clean up material when component unmounts or dependencies change
  useEffect(
    () => () => {
      if (gridMesh) {
        gridMesh.geometry.dispose()
        gridMesh.material.dispose()
      }
    },
    [gridMesh],
  )

  if (!selectedBlueprint?.sort_key) return null

  return (
    <primitive
      name="canvas-grid"
      object={gridMesh}
      visible={mode === Mode.Editor2D && gridOpacity > 0}
      renderOrder={-2}
    />
  )
}

export default CanvasGrid
